import { useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from './AuthContext';
import { gtmSetUserData } from '@/lib/gtmDataLayer';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const applySession = (nextSession: Session | null) => {
      if (!isMounted) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession);
      setIsAuthReady(true);

      if (event === 'SIGNED_IN' && nextSession?.user) {
        const authUser = nextSession.user;
        gtmSetUserData({
          email: authUser.email,
          firstName: authUser.user_metadata?.full_name?.split(' ')[0],
          lastName: authUser.user_metadata?.full_name?.split(' ').slice(1).join(' ') || undefined,
          phone: authUser.phone || undefined,
          userId: authUser.id,
        });
      }
    });

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      applySession(initialSession);
      if (isMounted) {
        setIsAuthReady(true);
      }
    });

    // Proactive session refresh: avoid stale tokens in long-lived preview tabs
    // (e.g. iframes during dev) which otherwise cause "not logged in" errors
    // when triggering edge functions after the tab has been idle.
    const SOON_MS = 120_000; // refresh if <2 min until expiry
    const refreshIfStale = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const s = data.session;
        if (!s) return;
        const expiresAt = (s.expires_at ?? 0) * 1000;
        const msLeft = expiresAt - Date.now();
        if (msLeft < SOON_MS) {
          await supabase.auth.refreshSession();
        }
      } catch {
        /* swallow — onAuthStateChange will surface real failures */
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshIfStale();
    };
    const onFocus = () => { void refreshIfStale(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    // Periodic safety net every 4 min in case visibility events don't fire
    const interval = window.setInterval(() => { void refreshIfStale(); }, 4 * 60 * 1000);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { full_name: fullName },
      },
    });

    if (!error && data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        email: email,
        full_name: fullName || null,
      });
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isAuthReady, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
